/**
 * Pannello "Anteprima diff import" per la pagina /materials/import.
 *
 * Mostra 4 card riepilogative:
 *  - Nuovi      : codici che saranno inseriti
 *  - Cambiati   : list_price diverso da quello in DB (con delta %)
 *  - Invariati  : list_price identico
 *  - Da rimuovere: in DB ma non nel file (solo se archive_missing è true)
 *
 * Click su una card → espande tabella dettaglio. Servizio solo read-only:
 * l'import effettivo resta su `useImportMaterials`.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ArrowUpDown, Minus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useImportDiff, type ImportDiffRow } from '@/hooks/useImportDiff';
import type { ParsedMaterial } from '@/lib/import/saintGobainParser';
import { formatNumber } from '@/utils/format';

interface ImportDiffPreviewProps {
  supplierId: string | null;
  rows: ParsedMaterial[] | null;
  archiveMissing: boolean;
}

type Section = 'new' | 'changed' | 'unchanged' | 'removed' | null;

function euro(v: number | null | undefined): string {
  if (v == null) return '—';
  return `€ ${formatNumber(v, 4)}`;
}

const ImportDiffPreview: React.FC<ImportDiffPreviewProps> = ({ supplierId, rows, archiveMissing }) => {
  const { data: diff, isLoading } = useImportDiff(supplierId, rows);
  const [expanded, setExpanded] = useState<Section>(null);

  if (!supplierId || !rows || rows.length === 0) return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Calcolo anteprima diff…
        </CardContent>
      </Card>
    );
  }

  if (!diff) return null;

  const tiles = [
    {
      key: 'new' as Section,
      label: 'Nuovi',
      icon: <Plus className="h-4 w-4" />,
      count: diff.newItems.length,
      color: 'text-blue-700 bg-blue-50 border-blue-200',
      items: diff.newItems,
    },
    {
      key: 'changed' as Section,
      label: 'Prezzi cambiati',
      icon: <ArrowUpDown className="h-4 w-4" />,
      count: diff.changedListPrice.length,
      color: 'text-amber-700 bg-amber-50 border-amber-200',
      items: diff.changedListPrice,
    },
    {
      key: 'unchanged' as Section,
      label: 'Invariati',
      icon: <Minus className="h-4 w-4" />,
      count: diff.unchanged.length,
      color: 'text-zinc-600 bg-zinc-50 border-zinc-200',
      items: diff.unchanged,
    },
    {
      key: 'removed' as Section,
      label: archiveMissing ? 'Saranno archiviati' : 'Non più nel file',
      icon: <Trash2 className="h-4 w-4" />,
      count: diff.removed.length,
      color: archiveMissing
        ? 'text-red-700 bg-red-50 border-red-200'
        : 'text-zinc-500 bg-zinc-50 border-zinc-200',
      items: diff.removed,
    },
  ];

  const renderRow = (r: ImportDiffRow, sec: Section) => (
    <tr key={r.code} className="border-t text-xs">
      <td className="py-1 px-2 font-mono">{r.code}</td>
      <td className="py-1 px-2 max-w-md truncate">{r.name}</td>
      {sec === 'changed' && (
        <>
          <td className="py-1 px-2 text-right tabular-nums">{euro(r.dbListPrice)}</td>
          <td className="py-1 px-2 text-right tabular-nums">{euro(r.fileListPrice)}</td>
          <td className="py-1 px-2 text-right tabular-nums">
            {r.deltaPct != null ? (
              <span className={r.deltaPct > 0 ? 'text-red-700 font-medium' : 'text-green-700 font-medium'}>
                {r.deltaPct > 0 ? '+' : ''}{formatNumber(r.deltaPct, 1)}%
              </span>
            ) : (
              '—'
            )}
          </td>
        </>
      )}
      {sec === 'new' && (
        <td className="py-1 px-2 text-right tabular-nums">{euro(r.fileListPrice)}</td>
      )}
      {sec === 'removed' && (
        <td className="py-1 px-2 text-right tabular-nums">{euro(r.dbListPrice)}</td>
      )}
      {sec === 'unchanged' && (
        <td className="py-1 px-2 text-right tabular-nums text-muted-foreground">{euro(r.dbListPrice)}</td>
      )}
    </tr>
  );

  const expandedTile = tiles.find((t) => t.key === expanded);

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div>
          <h4 className="text-sm font-semibold mb-1">Anteprima impatto sul catalogo</h4>
          <p className="text-xs text-muted-foreground">
            Clicca su una card per il dettaglio. L'import resta read-only finché non confermi.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {tiles.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setExpanded(expanded === t.key ? null : t.key)}
              className={`rounded-md border px-3 py-2 text-left transition ${t.color} ${
                expanded === t.key ? 'ring-2 ring-offset-1 ring-current' : 'hover:brightness-95'
              } ${t.count === 0 ? 'opacity-50 cursor-default pointer-events-none' : 'cursor-pointer'}`}
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold">
                  {t.icon}
                  {t.label}
                </span>
                {expanded === t.key ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </div>
              <div className="text-2xl font-bold tabular-nums mt-1">{t.count}</div>
            </button>
          ))}
        </div>

        {expanded && expandedTile && expandedTile.count > 0 && (
          <div className="border rounded-md overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="text-left py-1 px-2 font-semibold">Codice</th>
                    <th className="text-left py-1 px-2 font-semibold">Nome</th>
                    {expanded === 'changed' && (
                      <>
                        <th className="text-right py-1 px-2 font-semibold">DB</th>
                        <th className="text-right py-1 px-2 font-semibold">File</th>
                        <th className="text-right py-1 px-2 font-semibold">Δ %</th>
                      </>
                    )}
                    {expanded === 'new' && (
                      <th className="text-right py-1 px-2 font-semibold">Listino file</th>
                    )}
                    {expanded === 'removed' && (
                      <th className="text-right py-1 px-2 font-semibold">Listino DB</th>
                    )}
                    {expanded === 'unchanged' && (
                      <th className="text-right py-1 px-2 font-semibold">Listino</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {expandedTile.items.slice(0, 500).map((r) => renderRow(r, expanded))}
                </tbody>
              </table>
              {expandedTile.items.length > 500 && (
                <div className="text-xs text-muted-foreground text-center py-2 border-t bg-muted/50">
                  Mostrate prime 500 di {expandedTile.items.length}.
                </div>
              )}
            </div>
          </div>
        )}

        {diff.removed.length > 0 && !archiveMissing && (
          <Badge variant="outline" className="text-xs gap-1">
            <Trash2 className="h-3 w-3" />
            {diff.removed.length} non sono nel file ma resteranno attivi
            (toggle "Archivia mancanti" per nasconderli)
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

export default ImportDiffPreview;
